/**
 * Temporal API utilities — a bridge between the existing Date-based models
 * and the TC39 Temporal API (via temporal-polyfill).
 *
 * Usage:
 *   import { toZoned, nowZoned, isBefore, diffIn, toDate } from 'src/lib/utilities/temporal';
 *
 *   const start = toZoned(course.start_date, timezone);
 *   const end = start.add({ weeks: 6 });
 *   const daysLeft = diffIn(nowZoned(), end, 'day');
 *   this.formControl.setValue(toDate(end));
 */

import 'temporal-polyfill/global';

import { isNumber, isString } from './compare';
import { sanitizeForPhpTimestamp } from './convert';

export function parseInstant(value: unknown): Temporal.Instant | null {
	if (isString(value)) {
		try {
			return Temporal.Instant.from(value);
		} catch {
			return null;
		}
	} else if (isNumber(value)) {
		try {
			return Temporal.Instant.fromEpochMilliseconds(
				sanitizeForPhpTimestamp(value),
			);
		} catch {
			return null;
		}
	} else {
		return null;
	}
}

export function parseZoned(
	value: unknown,
	timezone?: string,
): Temporal.ZonedDateTime | null {
	if (isString(value)) {
		try {
			const val = Temporal.ZonedDateTime.from(value);
			if (timezone) {
				return val.withTimeZone(timezone);
			}
			return val;
		} catch {
			return null;
		}
	} else if (isNumber(value)) {
		try {
			const tz = timezone ?? Temporal.Now.timeZoneId();
			return Temporal.Instant.fromEpochMilliseconds(
				sanitizeForPhpTimestamp(value),
			).toZonedDateTimeISO(tz);
		} catch {
			return null;
		}
	} else {
		return null;
	}
}

/* ── Types ────────────────────────────────────────────────── */

/** Any date-like value accepted by comparison / arithmetic helpers. */
export type DateLike = Date | Temporal.ZonedDateTime | Temporal.Instant;

/* ── Internal helpers ─────────────────────────────────────── */

function toEpoch(v: DateLike): number {
	if (v instanceof Date) return v.getTime();
	if (v instanceof Temporal.Instant) return v.epochMilliseconds;
	return (v as Temporal.ZonedDateTime).epochMilliseconds;
}

/* ── On-ramp: Date → Temporal ─────────────────────────────── */

/**
 * Convert a JS Date or Temporal date value to a `Temporal.ZonedDateTime` in the
 * given (or local) timezone.
 *
 * This is the main entry point for working with Temporal on model Date fields:
 * ```ts
 * const zdt = toZoned(course.start_date, 'America/Denver');
 * ```
 */
export function toZoned(
	value: Date | Temporal.PlainDate | Temporal.Instant | Temporal.ZonedDateTime,
	timezone?: string,
): Temporal.ZonedDateTime {
	timezone = timezone || Temporal.Now.timeZoneId();

	if (value instanceof Temporal.PlainDate) {
		return value.toZonedDateTime(timezone);
	} else if (value instanceof Temporal.ZonedDateTime) {
		return value.withTimeZone(timezone);
	} else if (value instanceof Temporal.Instant) {
		return value.toZonedDateTimeISO(timezone);
	} else {
		return Temporal.Instant.fromEpochMilliseconds(
			value.getTime(),
		).toZonedDateTimeISO(timezone);
	}
}

/**
 * Get the current moment as a `Temporal.ZonedDateTime` (millisecond precision).
 */
export function nowZoned(timezone?: string): Temporal.ZonedDateTime {
	return Temporal.Instant.fromEpochMilliseconds(Date.now()).toZonedDateTimeISO(
		timezone ?? Temporal.Now.timeZoneId(),
	);
}

/* ── Off-ramp: Temporal → Date ────────────────────────────── */

/**
 * Convert a `Temporal.ZonedDateTime` (or Instant) back to a JS Date.
 *
 * Use this when you need to hand a value back to a form control,
 * date picker, or API argument that expects `Date`.
 */
export function toDate(value: Temporal.ZonedDateTime | Temporal.Instant): Date {
	return new Date(value.epochMilliseconds);
}

/**
 * Convert a calendar-only `Temporal.PlainDate` to a local JS Date.
 *
 * Use this for controls and utilities that still require `Date` but are working
 * with date-only values, such as birthdays or registration dates.
 */
export function plainDateToDate(
	value: Temporal.PlainDate | null | undefined,
): Date | null {
	if (!value) return null;

	return new Date(value.year, value.month - 1, value.day, 12);
}

/* ── Epoch conversion ─────────────────────────────────────── */

/**
 * Returns epoch milliseconds for any date-like value.
 */
export function epochMs(value: DateLike): number {
	return toEpoch(value);
}

/* ── Comparison helpers ───────────────────────────────────── */

/** True if `a` is strictly before `b`. */
export function isBefore(a: DateLike, b: DateLike): boolean {
	return toEpoch(a) < toEpoch(b);
}

/** True if `a` is strictly after `b`. */
export function isAfter(a: DateLike, b: DateLike): boolean {
	return toEpoch(a) > toEpoch(b);
}

/** True if `value` falls between `start` and `end`. */
export function isBetween(
	value: DateLike,
	start: DateLike,
	end: DateLike,
	inclusive: boolean = false,
): boolean {
	const v = toEpoch(value);
	const s = toEpoch(start);
	const e = toEpoch(end);
	return inclusive ? v >= s && v <= e : v > s && v < e;
}

/**
 * Compare two date-likes by calendar date in the given timezone.
 * Returns true if both fall on the same year-month-day.
 */
export function isSameDay(
	a: DateLike,
	b: DateLike,
	timezone?: string,
): boolean {
	const tz = timezone ?? Temporal.Now.timeZoneId();
	const pdA = Temporal.Instant.fromEpochMilliseconds(toEpoch(a))
		.toZonedDateTimeISO(tz)
		.toPlainDate();
	const pdB = Temporal.Instant.fromEpochMilliseconds(toEpoch(b))
		.toZonedDateTimeISO(tz)
		.toPlainDate();
	return pdA.equals(pdB);
}

/**
 * Compare two date-likes by calendar month in the given timezone.
 * Returns true if both fall in the same year-month.
 */
export function isSameMonth(
	a: DateLike,
	b: DateLike,
	timezone?: string,
): boolean {
	const tz = timezone ?? Temporal.Now.timeZoneId();
	const zdtA = Temporal.Instant.fromEpochMilliseconds(
		toEpoch(a),
	).toZonedDateTimeISO(tz);
	const zdtB = Temporal.Instant.fromEpochMilliseconds(
		toEpoch(b),
	).toZonedDateTimeISO(tz);
	return zdtA.year === zdtB.year && zdtA.month === zdtB.month;
}

/* ── Duration / diff ──────────────────────────────────────── */

type DiffUnit = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';

const MS_PER: Record<Exclude<DiffUnit, 'year' | 'month'>, number> = {
	second: 1_000,
	minute: 60_000,
	hour: 3_600_000,
	day: 86_400_000,
};

/**
 * Approximate difference between two date-likes.
 *
 * Returns a signed number — positive when `b` is after `a`.
 *
 * For 'year' and 'month', uses Temporal's calendar-aware `since()`.
 * For smaller units, uses simple ms-based division (no DST adjustment).
 *
 * ```ts
 * diffIn(start, end, 'day')   // → 42
 * diffIn(today, birthday, 'year')  // → -18
 * ```
 */
export function diffIn(a: DateLike, b: DateLike, unit: DiffUnit): number {
	if (unit === 'year' || unit === 'month') {
		const tz = Temporal.Now.timeZoneId();
		const zdtA = Temporal.Instant.fromEpochMilliseconds(
			toEpoch(a),
		).toZonedDateTimeISO(tz);
		const zdtB = Temporal.Instant.fromEpochMilliseconds(
			toEpoch(b),
		).toZonedDateTimeISO(tz);
		const dur = zdtA.until(zdtB, {
			largestUnit: `${unit}s` as 'years' | 'months',
		});
		return unit === 'year' ? dur.years : dur.years * 12 + dur.months;
	}

	const ms = toEpoch(b) - toEpoch(a);
	return Math.trunc(ms / MS_PER[unit]);
}

/* ── Month boundary helpers ───────────────────────────────── */

/**
 * Returns a `Temporal.ZonedDateTime` at midnight on the first day
 * of the month for the given date-like value.
 */
export function startOfMonth(
	value: DateLike,
	timezone?: string,
): Temporal.ZonedDateTime {
	const tz = timezone ?? Temporal.Now.timeZoneId();
	const zdt = Temporal.Instant.fromEpochMilliseconds(
		toEpoch(value),
	).toZonedDateTimeISO(tz);
	return zdt.with({
		day: 1,
		hour: 0,
		minute: 0,
		second: 0,
		millisecond: 0,
		microsecond: 0,
		nanosecond: 0,
	});
}

/**
 * Returns a `Temporal.ZonedDateTime` at the last millisecond
 * of the last day of the month for the given date-like value.
 * (23:59:59.999)
 */
export function endOfMonth(
	value: DateLike,
	timezone?: string,
): Temporal.ZonedDateTime {
	const tz = timezone ?? Temporal.Now.timeZoneId();
	const zdt = Temporal.Instant.fromEpochMilliseconds(
		toEpoch(value),
	).toZonedDateTimeISO(tz);
	return zdt.with({
		day: zdt.daysInMonth,
		hour: 23,
		minute: 59,
		second: 59,
		millisecond: 999,
		microsecond: 0,
		nanosecond: 0,
	});
}
